import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { jwtDecode } from 'jwt-decode';
import { googleAuthService } from './googleAuth';
import { AxiosTimeoutClassifier } from './errors/AxiosTimeoutClassifier';
import { TripCreationTimeoutError } from './errors/TripCreationTimeoutError';
import { 
  Trip, 
  NewTripRequest, 
  TripResponse, 
  TripMetadataResponse,
  MockTripsResponse, 
  MockSummaryResponse 
} from '../types';

// API base configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

// Helper: simple JWT expiry check
const isJwtExpired = (token: string): boolean => {
  try {
    const payload: any = jwtDecode(token);
    if (!payload?.exp) return false;
    const nowMs = Date.now();
    return payload.exp * 1000 <= nowMs;
  } catch {
    return true;
  }
};

// Helper function to get auth token
const getAuthToken = async (): Promise<string | null> => {
  try {
    // Prefer Cognito (Amplify) session tokens first (more reliable, auto-refreshed)
    console.log('🔍 getAuthToken: Fetching auth session...');
    const session = await fetchAuthSession();
    console.log('🔍 getAuthToken: Session data:', session);
    console.log('🔍 getAuthToken: Tokens object:', session.tokens);
    
    // Try different token sources
    let token = null;
    
    if (session.tokens?.accessToken) {
      token = session.tokens.accessToken.toString();
      console.log('✅ getAuthToken: Using tokens.accessToken');
    } else if (session.tokens?.idToken) {
      token = session.tokens.idToken.toString();
      console.log('✅ getAuthToken: Using tokens.idToken as fallback');
    }
    
    if (token) {
      console.log('🔍 getAuthToken: Token (first 50 chars):', token.substring(0, 50) + '...');
      // Double-check if token is expired
      if (isJwtExpired(token)) {
        console.log('⚠️ getAuthToken: Token still expired after refresh, clearing tokens');
        // Token is expired even after refresh attempt, user needs to re-authenticate
        return null;
      } else {
        return token;
      }
    } else {
      console.log('ℹ️ getAuthToken: No Cognito token in session. Checking Google auth service...');
      // Next, check for Google OAuth token using the auth service
      try {
        const googleToken = await googleAuthService.getValidAccessToken();
        if (googleToken) {
          console.log('✅ getAuthToken: Using Google OAuth token from auth service');
          console.log('🔍 getAuthToken: Google token (first 50 chars):', googleToken.substring(0, 50) + '...');
          return googleToken;
        }
      } catch (error) {
        console.log('❌ getAuthToken: Google auth service error:', error);
      }
      
      console.log('❌ getAuthToken: No token found in session or valid Google token, trying manual parsing...');
      
      // Fallback: manually parse tokens from localStorage
      try {
        const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.LastAuthUser');
        if (lastAuthUser) {
          const accessTokenKey = `CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.${lastAuthUser}.accessToken`;
          const accessToken = localStorage.getItem(accessTokenKey);
          if (accessToken) {
            if (isJwtExpired(accessToken)) {
              console.log('❌ getAuthToken: Stored access token expired');
            } else {
              console.log('✅ getAuthToken: Found access token in localStorage');
              return accessToken;
            }
          }
          
          const idTokenKey = `CognitoIdentityServiceProvider.5komk2o44f503r6pj1m283nhtr.${lastAuthUser}.idToken`;
          const idToken = localStorage.getItem(idTokenKey);
          if (idToken) {
            if (isJwtExpired(idToken)) {
              console.log('❌ getAuthToken: Stored idToken expired');
            } else {
              console.log('✅ getAuthToken: Found ID token in localStorage as fallback');
              return idToken;
            }
          }
        }
      } catch (error) {
        console.log('❌ getAuthToken: Manual token parsing failed:', error);
      }
      
      console.log('❌ getAuthToken: No token found anywhere');
      return null;
    }
  } catch (error) {
    console.log('❌ getAuthToken: Error fetching auth session:', error);
    return null;
  }
};

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased to 1 minute for regular operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate client for AI operations with longer timeout
const aiApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes for AI operations (trip generation can take time)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
const addAuthInterceptor = (client: typeof apiClient) => {
  client.interceptors.request.use(
    async (config) => {
      console.log('🔍 Request interceptor: Adding auth token to request');
      console.log('🔍 Request URL:', config.url);
      console.log('🔍 Request method:', config.method);
      const token = await getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('✅ Request interceptor: Authorization header added');
        console.log('🔍 Request headers:', config.headers);
        console.log('🔍 Full token (first 100 chars):', token.substring(0, 100) + '...');
      } else {
        console.log('❌ Request interceptor: No token available');
        console.log('🔍 Request headers:', config.headers);
      }
      return config;
    },
    (error) => {
      console.log('❌ Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle auth errors
  client.interceptors.response.use(
    (response) => {
      console.log('✅ Response received:', response.status, response.config.url);
      return response;
    },
    async (error) => {
      console.log('❌ Response error:', error.response?.status, error.config?.url);
      console.log('🔍 Error details:', error.response?.data);
      if (error.response?.status === 401) {
        // Token expired or invalid. If we used a Google token, clear and retry once.
        console.log('❌ Authentication failed - 401 Unauthorized');
        console.log('🔍 Error details:', error.response?.data);
        const originalRequest = error.config as any;
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          // Try Cognito force refresh first
          try {
            console.log('🔄 Attempting force token refresh');
            const refreshed = await (fetchAuthSession as any)({ forceRefresh: true });
            const newToken = refreshed.tokens?.accessToken?.toString() || refreshed.tokens?.idToken?.toString() || null;
            if (newToken) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              console.log('✅ Force refresh worked, retrying request');
              return client(originalRequest);
            }
          } catch (refreshErr) {
            console.log('❌ Force refresh failed:', refreshErr);
          }
          // Try to get a fresh Google token using the auth service
          try {
            const newToken = await googleAuthService.getValidAccessToken();
            if (newToken) {
              console.log('🔄 Attempting to retry request with refreshed Google token');
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return client(originalRequest);
            }
          } catch (refreshError) {
            console.log('❌ Google token refresh failed:', refreshError);
          }
        }
      }
      return Promise.reject(error);
    }
  );
};

// Apply auth interceptors to both clients
addAuthInterceptor(apiClient);
addAuthInterceptor(aiApiClient);

// API service class
export class TripPlannerAPI {
  // Get a specific trip by ID
  static async getTrip(tripId: string): Promise<Trip> {
    try {
      const response = await apiClient.get<{ success: boolean; message: string; trip: Trip }>(`/trips/${tripId}`);
      if (response.data.success && response.data.trip) {
        return response.data.trip;
      } else {
        throw new Error(response.data.message || 'Failed to fetch trip');
      }
    } catch (error) {
      console.error('Error fetching trip:', error);
      throw new Error('Failed to fetch trip');
    }
  }

  // Get trip metadata for efficient listing (NEW - PERFORMANCE OPTIMIZED)
  static async getUserTripsMetadata(userId: string): Promise<TripMetadataResponse> {
    try {
      const response = await apiClient.get<TripMetadataResponse>(`/api/users/${userId}/trips`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user trip metadata:', error);
      throw new Error('Failed to fetch user trip metadata');
    }
  }

  // Get all trips for a specific user (DEPRECATED - use getUserTripsMetadata for listing)
  static async getUserTrips(userId: string): Promise<{ success: boolean; message: string; trips: Trip[]; count: number }> {
    try {
      const response = await apiClient.get(`/api/users/${userId}/trips/full`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user trips:', error);
      throw new Error('Failed to fetch user trips');
    }
  }

  // Get mock trip summary
  static async getMockSummary(): Promise<MockSummaryResponse> {
    try {
      const response = await apiClient.get<MockSummaryResponse>('/mock/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching mock summary:', error);
      throw new Error('Failed to fetch mock summary');
    }
  }

  // Get multiple mock trips
  static async getMockTrips(count: number = 3): Promise<MockTripsResponse> {
    try {
      const response = await apiClient.get<MockTripsResponse>(`/mock/trips?count=${count}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching mock trips:', error);
      throw new Error('Failed to fetch mock trips');
    }
  }

  // Get a single mock trip
  static async getMockTrip(tripId?: string): Promise<TripResponse> {
    try {
      const response = await apiClient.get<TripResponse>('/mock/trip', {
        params: tripId ? { trip_id: tripId } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching mock trip:', error);
      throw new Error('Failed to fetch mock trip');
    }
  }

  // Create a new trip
  static async createTrip(tripData: NewTripRequest): Promise<TripResponse> {
    try {
      const response = await apiClient.post<TripResponse>('/trips/create', tripData);
      return response.data;
    } catch (error) {
      console.error('Error creating trip:', error);
      throw new Error('Failed to create trip');
    }
  }

  // Create a new AI-generated trip
  static async createAITrip(tripData: NewTripRequest): Promise<{ success: boolean; message: string; tripId: string; destination: string; startDate: string; endDate: string; totalDays: number; totalItems: number }> {
    try {
      console.log('Creating AI trip with extended timeout...');
      const response = await aiApiClient.post('/api/trips', tripData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating AI trip:', error);
      
      // Handle timeout specifically (client timeout or gateway timeout)
      if (AxiosTimeoutClassifier.isTimeout(error)) {
        throw new TripCreationTimeoutError(
          "Trip creation is taking longer than expected. It may still complete in the background—please check it in 'My Trips'."
        );
      }
      // Propagate backend error details when available
      const status = error?.response?.status;
      const serverDetail = error?.response?.data?.detail || error?.response?.data?.message;
      if (status === 401) {
        throw new Error(serverDetail || 'Unauthorized. Please sign in again.');
      }
      if (status === 403) {
        throw new Error(serverDetail || 'Forbidden. Your account may not have access to create AI trips.');
      }
      if (serverDetail) {
        throw new Error(serverDetail);
      }
      
      throw new Error('Failed to create AI-generated trip');
    }
  }

  // Get example trip structure
  static async getExampleTrip(): Promise<Trip> {
    try {
      const response = await apiClient.get<Trip>('/models/example');
      return response.data;
    } catch (error) {
      console.error('Error fetching example trip:', error);
      throw new Error('Failed to fetch example trip');
    }
  }

  // Health check
  static async healthCheck(): Promise<{ message: string }> {
    try {
      const response = await apiClient.get<{ message: string }>('/');
      return response.data;
    } catch (error) {
      console.error('Error checking API health:', error);
      throw new Error('API is not responding');
    }
  }

  // Add a new day to an existing trip
  static async addDayToTrip(tripId: string, dayData: {
    dayDate: string;
    title: string;
    summary: string;
    lodgingInfo: string;
  }): Promise<{ success: boolean; message: string; day: any }> {
    try {
      const response = await apiClient.post(`/api/trips/${tripId}/days`, dayData);
      return response.data;
    } catch (error: any) {
      console.error('Error adding day to trip:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to add day to trip';
      throw new Error(errorMessage);
    }
  }

  // Add a new itinerary item to a specific day
  static async addItemToDay(tripId: string, dayId: string, itemData: {
    name: string;
    placeName: string;
    description: string;
    startTime: string;
    endTime: string;
    location: { lat: number; lon: number };
    itemType: string;
    estimatedCost: number;
    isDone?: boolean;
  }): Promise<{ success: boolean; message: string; item: any }> {
    try {
      const response = await apiClient.post(`/api/trips/${tripId}/days/${dayId}/items`, itemData);
      return response.data;
    } catch (error: any) {
      console.error('Error adding item to day:', error);
      console.error('Error response:', error.response?.data);
      console.error('Request data:', itemData);
      
      // Extract detailed validation error if available
      let errorMessage = 'Failed to add item to day';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          errorMessage = error.response.data.detail.map((err: any) => 
            `${err.loc?.join('.')}: ${err.msg}`
          ).join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  // Generate a new day using AI
  static async aiGenerateDay(tripId: string, prompt: string): Promise<{ success: boolean; message: string; day: any }> {
    try {
      const response = await aiApiClient.post(`/api/trips/${tripId}/days/ai-generate`, { prompt });
      return response.data;
    } catch (error: any) {
      console.error('Error generating day with AI:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate day with AI';
      throw new Error(errorMessage);
    }
  }

  // Generate a new itinerary item using AI
  static async aiGenerateItem(tripId: string, dayId: string, prompt: string): Promise<{ success: boolean; message: string; item: any }> {
    try {
      const response = await aiApiClient.post(`/api/trips/${tripId}/days/${dayId}/items/ai-generate`, { prompt });
      return response.data;
    } catch (error: any) {
      console.error('Error generating item with AI:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate item with AI';
      throw new Error(errorMessage);
    }
  }

  // Generate item preview using AI (without saving to database)
  static async aiGenerateItemPreview(tripId: string, dayId: string, prompt: string): Promise<{ success: boolean; message: string; item: any }> {
    try {
      const response = await apiClient.post(`/api/trips/${tripId}/days/${dayId}/items/ai-generate-preview`, {
        prompt
      });
      return response.data;
    } catch (error: any) {
      console.error('Error generating item preview with AI:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate item preview with AI';
      throw new Error(errorMessage);
    }
  }

  static async aiEditItem(tripId: string, dayId: string, itemId: string, editDescription: string): Promise<{ success: boolean; message: string; item: any }> {
    try {
      const response = await aiApiClient.post(`/api/trips/${tripId}/days/${dayId}/items/${itemId}/ai-edit`, { editDescription });
      return response.data;
    } catch (error: any) {
      console.error('Error editing item with AI:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to edit item with AI';
      throw new Error(errorMessage);
    }
  }

  static async aiEditDay(tripId: string, dayId: string, editDescription: string): Promise<{ success: boolean; message: string; day: any }> {
    try {
      const response = await aiApiClient.post(`/api/trips/${tripId}/days/${dayId}/ai-edit`, { editDescription });
      return response.data;
    } catch (error: any) {
      console.error('Error editing day with AI:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to edit day with AI';
      throw new Error(errorMessage);
    }
  }

  // Generate a new day's metadata using AI (for AddDayModal AI mode)
  static async aiGenerateNewDay(tripId: string, prompt: string, suggestedDate?: string): Promise<{ success: boolean; message: string; dayDate: string; title: string; summary: string; lodgingInfo: string }> {
    try {
      const response = await aiApiClient.post(`/api/trips/${tripId}/days/ai-generate-new`, { 
        prompt,
        suggestedDate 
      });
      return response.data;
    } catch (error: any) {
      console.error('Error generating new day with AI:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate day with AI';
      throw new Error(errorMessage);
    }
  }

  // Generate enhanced day summary and lodging info using AI
  static async aiGenerateDaySummary(tripId: string, summary: string): Promise<{ success: boolean; message: string; summary: string; lodgingInfo: string }> {
    try {
      const response = await aiApiClient.post(`/api/trips/${tripId}/days/ai-generate-summary`, {
        summary
      });
      return response.data;
    } catch (error: any) {
      console.error('Error generating enhanced summary:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate enhanced summary';
      throw new Error(errorMessage);
    }
  }

  // Add day with AI-generated activities
  static async addDayWithActivities(tripId: string, dayData: {
    dayDate: string;
    title: string;
    summary: string;
    lodgingInfo: string;
  }): Promise<{ success: boolean; message: string; day: any }> {
    try {
      const response = await aiApiClient.post(`/api/trips/${tripId}/days/ai-generate`, {
        prompt: `Create a day with the following details: Title: ${dayData.title}. Summary: ${dayData.summary}. Lodging: ${dayData.lodgingInfo || 'Not specified'}. Date: ${dayData.dayDate}`
      });
      return response.data;
    } catch (error: any) {
      console.error('Error adding day with activities:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to add day with activities';
      throw new Error(errorMessage);
    }
  }

  // Override existing day with AI-generated activities
  static async overrideDayWithActivities(tripId: string, dayId: string, dayData: {
    dayDate: string;
    title: string;
    summary: string;
    lodgingInfo: string;
  }): Promise<{ success: boolean; message: string; day: any }> {
    try {
      const response = await aiApiClient.post(`/api/trips/${tripId}/days/${dayId}/ai-override`, {
        prompt: `Create a day with the following details: Title: ${dayData.title}. Summary: ${dayData.summary}. Lodging: ${dayData.lodgingInfo || 'Not specified'}. Date: ${dayData.dayDate}`
      });
      return response.data;
    } catch (error: any) {
      console.error('Error overriding day with activities:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to override day with activities';
      throw new Error(errorMessage);
    }
  }

  // Generate recommendations for a day
  static async generateRecommendations(tripId: string, dayId: string, existingActivities: string[] = []): Promise<{ success: boolean; message: string; recommendations: any[] }> {
    try {
      const response = await aiApiClient.post(`/api/trips/${tripId}/days/${dayId}/recommendations`, {
        daySummary: "placeholder", // This will be ignored, we use the actual day's summary from DB
        destination: "placeholder", // This will be ignored, we use the actual destination from DB
        existingActivities
      });
      return response.data;
    } catch (error: any) {
      console.error('Error generating recommendations:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate recommendations';
      throw new Error(errorMessage);
    }
  }

  // Add activity from recommendation
  static async addActivityFromRecommendation(tripId: string, dayId: string, recommendationId: string, targetDayDate: string): Promise<{ success: boolean; message: string; item: any }> {
    try {
      const response = await apiClient.post(`/api/trips/${tripId}/days/${dayId}/recommendations/${recommendationId}/add-activity`, {
        recommendationId,
        targetDayDate
      });
      return response.data;
    } catch (error: any) {
      console.error('Error adding activity from recommendation:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to add activity from recommendation';
      throw new Error(errorMessage);
    }
  }

  // Dislike a recommendation
  static async dislikeRecommendation(tripId: string, dayId: string, recommendationId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post(`/api/trips/${tripId}/days/${dayId}/recommendations/${recommendationId}/dislike`);
      return response.data;
    } catch (error: any) {
      console.error('Error disliking recommendation:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to dislike recommendation';
      throw new Error(errorMessage);
    }
  }

  // Update day metadata
  static async updateDay(tripId: string, dayId: string, dayData: {
    dayDate: string;
    summary: string;
    lodgingInfo: string;
  }): Promise<{ success: boolean; message: string; day?: any }> {
    try {
      const response = await apiClient.put(`/api/trips/${tripId}/days/${dayId}`, dayData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating day:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update day';
      throw new Error(errorMessage);
    }
  }

  // Delete day
  static async deleteDay(tripId: string, dayId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete(`/api/trips/${tripId}/days/${dayId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting day:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete day';
      throw new Error(errorMessage);
    }
  }

  // Update trip metadata
  static async updateTrip(tripId: string, tripData: {
    destination: string;
    startDate: string;
    endDate: string;
    travelers: number;
    budget: number;
    interests: string[];
    coverImage?: string;
  }): Promise<{ success: boolean; message: string; trip?: any }> {
    try {
      const response = await apiClient.put(`/trips/${tripId}`, tripData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating trip:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update trip';
      throw new Error(errorMessage);
    }
  }

  // Delete trip
  static async deleteTrip(tripId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete(`/trips/${tripId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting trip:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete trip';
      throw new Error(errorMessage);
    }
  }

  // Update item
  static async updateItem(tripId: string, dayId: string, itemId: string, itemData: {
    name: string;
    placeName: string;
    description: string;
    startTime: string;
    endTime: string;
    location: { lat: number; lon: number };
    itemType: string;
    estimatedCost: number;
    officialUrl?: string;
    isDone?: boolean;
  }): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.put(`/trips/${tripId}/days/${dayId}/items/${itemId}`, itemData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating item:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update item';
      throw new Error(errorMessage);
    }
  }

  // Delete item
  static async deleteItem(tripId: string, dayId: string, itemId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete(`/trips/${tripId}/days/${dayId}/items/${itemId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting item:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete item';
      throw new Error(errorMessage);
    }
  }

  // Get route legs between locations
  static async getRoute(
    origin: { lat: number; lon: number },
    destination: { lat: number; lon: number },
    waypoints?: Array<{ lat: number; lon: number }>
  ): Promise<{ success: boolean; legs: Array<{ start_location: { lat: number; lon: number }; end_location: { lat: number; lon: number }; polyline: string; duration: number; distance: number }> }> {
    try {
      const response = await apiClient.post('/api/routes', {
        origin: { lat: origin.lat, lon: origin.lon },
        destination: { lat: destination.lat, lon: destination.lon },
        waypoints: waypoints?.map(wp => ({ lat: wp.lat, lon: wp.lon }))
      });
      return response.data;
    } catch (error: any) {
      console.error('Error getting route:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to get route';
      throw new Error(errorMessage);
    }
  }
}

// Utility functions for API responses
export const formatCost = (costInCents: number): string => {
  return `$${(costInCents / 100).toFixed(2)}`;
};

export const formatTime = (time: string): string => {
  // Convert 24-hour format to 12-hour format
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};


// Export the apiClient for direct use if needed
export { apiClient };
